import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Download, ImageOff } from 'lucide-react';

/**
 * Gallery grid for 2+ media messages sent as a group.
 * 1 item → full bubble (handled by caller via MediaBubble).
 * 2 items → side-by-side.
 * 3 items → 1 large + 2 stacked right.
 * 4 items → 2x2 grid.
 * 5+      → 2x2 grid with "+N" overlay on last tile.
 */

// Per-tile media renderer with its own broken-state — so a single 404
// only blanks that one tile, never crashes the gallery layout.
const GalleryTile = ({ url, isImage, isVideo }) => {
  const [broken, setBroken] = useState(false);
  if (broken || !url) {
    return (
      <span className="absolute inset-0 flex flex-col items-center justify-center bg-amber-50 text-amber-700 gap-1">
        <ImageOff size={20} />
        <span className="text-[10px] font-medium">Unavailable</span>
      </span>
    );
  }
  if (isImage) {
    return (
      <img src={url} alt="" className="w-full h-full object-cover"
           onError={() => setBroken(true)} />
    );
  }
  if (isVideo) {
    return (
      <>
        <video src={url} className="w-full h-full object-cover" muted preload="metadata"
               onError={() => setBroken(true)} />
        <span className="absolute inset-0 flex items-center justify-center bg-black/25">
          <span className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="#111"><path d="M8 5v14l11-7z" /></svg>
          </span>
        </span>
      </>
    );
  }
  return null;
};

const MediaGallery = ({ items }) => {
  const [viewerIndex, setViewerIndex] = useState(null);
  const n = items.length;
  if (n === 0) return null;

  const tiles = n <= 4 ? items : items.slice(0, 4);
  const extra = n > 4 ? n - 4 : 0;

  const open = (i) => setViewerIndex(i);
  const close = () => setViewerIndex(null);
  const prev = () => setViewerIndex(i => (i - 1 + n) % n);
  const next = () => setViewerIndex(i => (i + 1) % n);

  const layout =
    n === 2 ? 'grid-cols-2 grid-rows-1 aspect-[2/1]' :
    n === 3 ? 'grid-cols-2 grid-rows-2 aspect-[4/3]' :
    /* 4+ */  'grid-cols-2 grid-rows-2 aspect-square';

  return (
    <>
      <div className={`grid ${layout} gap-0.5 rounded-lg overflow-hidden bg-black/5 max-w-xs`}
        data-testid="inbox-media-gallery">
        {tiles.map((m, i) => {
          const url = m.mediaUrl || m.media_url;
          const isImage = (m.messageType || m.type) === 'image';
          const isVideo = (m.messageType || m.type) === 'video';
          // 3-item special: first tile spans both rows
          const spanClass = (n === 3 && i === 0) ? 'row-span-2' : '';
          const showOverlay = (i === 3 && extra > 0);
          return (
            <button
              key={m.id || i}
              onClick={() => open(i)}
              className={`relative block bg-black/10 ${spanClass} cursor-zoom-in hover:opacity-95 transition-opacity overflow-hidden`}
              data-testid={`inbox-media-tile-${i}`}
            >
              <GalleryTile url={url} isImage={isImage} isVideo={isVideo} />
              {showOverlay && (
                <span className="absolute inset-0 bg-black/55 text-white text-xl font-semibold flex items-center justify-center">
                  +{extra}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Fullscreen viewer */}
      {viewerIndex !== null && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={close}
          data-testid="inbox-media-viewer">
          <button onClick={(e) => { e.stopPropagation(); close(); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
            <X size={20} />
          </button>
          {n > 1 && (
            <button onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
              <ChevronLeft size={22} />
            </button>
          )}
          <div onClick={(e) => e.stopPropagation()} className="max-w-[90vw] max-h-[85vh] flex items-center justify-center">
            {(() => {
              const m = items[viewerIndex];
              const url = m.mediaUrl || m.media_url;
              const type = m.messageType || m.type;
              const onErr = (e) => {
                e.currentTarget.outerHTML = `<div class="px-8 py-12 bg-amber-50 text-amber-800 rounded-lg flex flex-col items-center gap-2"><span class="text-sm font-medium">Media unavailable</span><span class="text-xs">This file may have expired or been removed.</span></div>`;
              };
              if (!url) return <div className="px-8 py-12 bg-amber-50 text-amber-800 rounded-lg text-sm">Media unavailable</div>;
              if (type === 'video') return <video src={url} controls autoPlay className="max-w-[90vw] max-h-[85vh]" onError={onErr} />;
              return <img src={url} alt="" className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg" onError={onErr} />;
            })()}
          </div>
          {n > 1 && (
            <button onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
              <ChevronRight size={22} />
            </button>
          )}
          <a href={items[viewerIndex].mediaUrl || items[viewerIndex].media_url}
            download target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-6 right-6 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-medium flex items-center gap-1.5">
            <Download size={13} /> Download
          </a>
        </div>
      )}
    </>
  );
};

export default MediaGallery;
